import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, articleContent } from '@/mocks/articles';
import { mainSiteUrl } from '@/mocks/categories';
import { articleSchema, breadcrumbSchema, faqSchema, siteUrl, organizationSchema } from '@/mocks/schema';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const article = articles.find((a) => a.slug === slug);
  const content = slug ? articleContent[slug] : null;

  if (!article || !content) {
    return (
      <div className="min-h-screen bg-white">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
        />
        <Navbar />
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-6">
          <div className="w-32 h-32 rounded-2xl overflow-hidden mb-8">
            <img
              src="https://readdy.ai/api/search-image?query=Fashion%20product%20flat%20lay%20on%20soft%20beige%20background%2C%20clean%20minimalist%20aesthetic%2C%20editorial%20photography%2C%20warm%20tones&width=300&height=300&seq=article-404&orientation=squarish"
              alt="not found"
              className="w-full h-full object-cover"
            />
          </div>
          <h1 className="text-2xl font-semibold text-stone-900">{t('404.title')}</h1>
          <p className="mt-2 text-stone-500">{t('404.subtitle')}</p>
          <a href="/" className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-stone-900 text-white text-sm font-medium rounded-full hover:bg-stone-800 transition-colors">
            {t('404.backHome')}
          </a>
        </div>
        <Footer />
      </div>
    );
  }

  // Build Schema.org JSON-LD
  const blogSchema = articleSchema({
    slug: article.slug,
    title: article.title,
    description: content.metaDescription,
    image: article.image,
    datePublished: '2026-07-02T10:00:00+03:00',
    dateModified: '2026-07-02T10:00:00+03:00',
    authorName: 'YOYBUY',
    category: article.category,
  });

  const breadcrumb = breadcrumbSchema([
    { name: 'Главная', url: siteUrl },
    { name: 'Статьи', url: siteUrl },
    { name: article.title, url: siteUrl + '/article/' + article.slug },
  ]);

  // Extract FAQ items for schema
  const faqBlock = content.content.find((b) => b.type === 'faq');
  const faqItems = faqBlock
    ? [
        {
          question: 'Безопасно ли использовать YOYBUY Spreadsheet?',
          answer: 'Да, YOYBUY использует защищённые платежные шлюзы, систему рейтингов продавцов и эскроу-платежи для защиты покупателей. Все транзакции полностью прозрачны.',
        },
        {
          question: 'Сколько времени занимает доставка из Китая?',
          answer: 'Доставка занимает от 10 до 25 дней в зависимости от выбранного способа и страны назначения. YOYBUY Spreadsheet показывает расчётные сроки до оплаты.',
        },
        {
          question: 'Какие товары можно заказывать через YOYBUY?',
          answer: 'Обувь, одежда, аксессуары, электроника, парфюмерия и многие другие категории товаров с китайских площадок Taobao, Weidian и 1688.',
        },
      ]
    : [];

  const relatedArticles = articles.filter((a) => a.slug !== slug).slice(0, 3);

  const contentGallery = [
    'https://readdy.ai/api/search-image?query=Flat%20lay%20sneakers%20and%20accessories%20on%20warm%20beige%20background%2C%20editorial%20product%20photography%2C%20soft%20natural%20lighting%2C%20clean%20minimalist%20aesthetic&width=800&height=500&seq=article-gal-01&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Fashion%20items%20neatly%20arranged%20on%20light%20neutral%20surface%2C%20bags%20watches%20caps%20in%20organized%20grid%2C%20editorial%20photography%2C%20soft%20shadows&width=800&height=500&seq=article-gal-02&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Product%20flat%20lay%20with%20sportswear%20accessories%20on%20soft%20beige%20background%2C%20clean%20aesthetic%2C%20warm%20tones%2C%20editorial%20style%20photography&width=800&height=500&seq=article-gal-03&orientation=landscape',
  ];

  return (
    <div className="min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(blogSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }}
      />
      {faqItems.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema(faqItems)) }}
        />
      )}
      <Navbar />

      {/* Article Hero */}
      <section className="pt-20 md:pt-24">
        <div className="w-full px-6 lg:px-10">
          <div className="relative aspect-[21/9] md:aspect-[3/1] rounded-2xl overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
            <div className="absolute top-8 right-10 w-16 h-16 rounded-xl overflow-hidden border-2 border-white/30 shadow-lg rotate-6 opacity-70 hidden md:block">
              <img
                src="https://readdy.ai/api/search-image?query=White%20sneaker%20isolated%20on%20clean%20background%2C%20minimalist%20ecommerce%20shot%2C%20soft%20lighting&width=160&height=160&seq=article-hero-float&orientation=squarish"
                alt="product"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <div className="flex items-center gap-3 mb-3">
                <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded-full">
                  {article.category}
                </span>
                <span className="text-white/60 text-xs">{article.readTime} {t('article.readTime')}</span>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-medium text-white leading-tight max-w-3xl">
                {article.title}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="w-full px-6 lg:px-10 py-4">
        <nav className="flex items-center gap-2 text-sm text-stone-500" aria-label="Breadcrumb">
          <a href="/" className="hover:text-stone-900 transition-colors">{t('nav.home')}</a>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
          <a href="/" className="hover:text-stone-900 transition-colors">{t('nav.articles')}</a>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
          <span className="text-stone-900 truncate max-w-xs">{article.title}</span>
        </nav>
      </div>

      {/* Article Content */}
      <section className="w-full px-6 lg:px-10 py-8 md:py-12">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
          {/* Main content */}
          <article className="lg:w-[75%]">
            {/* Meta */}
            <div className="flex items-center gap-4 text-sm text-stone-500 mb-8 pb-6 border-b border-stone-100">
              <span>{article.readTime} {t('article.readTime')}</span>
              <span>·</span>
              <span>{t('article.published')} 2026-07-02</span>
            </div>

            {/* Content blocks */}
            <div className="prose prose-stone max-w-none">
              {content.content.map((block, index) => {
                switch (block.type) {
                  case 'intro':
                    return (
                      <p key={index} className="text-lg md:text-xl text-stone-700 leading-relaxed mb-6 font-medium">
                        {block.text}
                      </p>
                    );
                  case 'h2':
                    return (
                      <h2 key={index} className="font-heading text-2xl md:text-3xl font-medium text-stone-900 mt-12 mb-6 leading-tight">
                        {block.text}
                      </h2>
                    );
                  case 'paragraph':
                    return (
                      <p key={index} className="text-base text-stone-700 leading-relaxed mb-6">
                        {block.text}
                      </p>
                    );
                  case 'list':
                    return (
                      <ul key={index} className="space-y-3 mb-8">
                        {block.items?.map((item, i) => (
                          <li key={i} className="flex items-start gap-3 text-base text-stone-700">
                            <span className="w-2 h-2 mt-2 rounded-full bg-rose-400 shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    );
                  case 'table':
                    return (
                      <div key={index} className="overflow-x-auto mb-8 rounded-xl border border-stone-200">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-stone-100">
                              {block.headers?.map((h) => (
                                <th key={h} className="px-4 py-3 text-left font-semibold text-stone-900">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {block.rows?.map((row, i) => (
                              <tr key={i} className="border-t border-stone-100 hover:bg-stone-50 transition-colors">
                                {block.headers?.map((h) => (
                                  <td key={h} className="px-4 py-3 text-stone-700">{row[h]}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  case 'faq':
                    return (
                      <div key={index} className="mb-8">
                        <h3 className="font-heading text-xl font-medium text-stone-900 mb-4">{t('article.faqTitle')}</h3>
                        <div dangerouslySetInnerHTML={{ __html: block.html || '' }} />
                      </div>
                    );
                  case 'cta':
                    return (
                      <div key={index} className="relative bg-stone-900 rounded-xl p-6 md:p-8 my-10 overflow-hidden">
                        <div className="absolute top-0 right-0 w-40 h-40 bg-rose-400/20 rounded-full blur-3xl" />
                        <div className="absolute bottom-0 left-0 w-32 h-32 bg-amber-400/15 rounded-full blur-3xl" />
                        <div className="relative z-10">
                          <p className="text-white text-base md:text-lg leading-relaxed mb-4">{block.text}</p>
                          <a
                            href={mainSiteUrl}
                            target="_blank"
                            rel="noopener nofollow"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-stone-900 text-sm font-semibold rounded-full hover:bg-stone-100 transition-colors"
                          >
                            {t('article.ctaButton')}
                            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                          </a>
                        </div>
                      </div>
                    );
                  default:
                    return null;
                }
              })}
            </div>

            {/* Mid-content gallery */}
            <div className="my-12 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {contentGallery.map((src, i) => (
                <div key={i} className="aspect-[4/3] rounded-xl overflow-hidden bg-stone-100">
                  <img src={src} alt="YOYBUY products" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
              ))}
            </div>

            {/* Back to home link */}
            <div className="mt-12 pt-8 border-t border-stone-100">
              <a
                href="/"
                className="inline-flex items-center gap-2 text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
                {t('article.backToHome')}
              </a>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="lg:w-[25%] space-y-8">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="block w-full text-center px-6 py-4 bg-stone-900 text-white text-sm font-semibold rounded-xl hover:bg-stone-800 transition-colors"
            >
              yoybuy sheet
            </a>

            <div className="bg-stone-50 rounded-xl p-5 space-y-3">
              <h3 className="font-medium text-stone-900 text-sm">Товары YOYBUY</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  'https://readdy.ai/api/search-image?query=Sneaker%20product%20shot%20isolated%20on%20light%20background%2C%20clean%20minimalist%20ecommerce&width=200&height=200&seq=side-prod-1&orientation=squarish',
                  'https://readdy.ai/api/search-image?query=Handbag%20product%20shot%20isolated%20on%20light%20background%2C%20clean%20minimalist%20ecommerce&width=200&height=200&seq=side-prod-2&orientation=squarish',
                  'https://readdy.ai/api/search-image?query=Watch%20product%20shot%20isolated%20on%20light%20background%2C%20clean%20minimalist%20ecommerce&width=200&height=200&seq=side-prod-3&orientation=squarish',
                  'https://readdy.ai/api/search-image?query=Cap%20product%20shot%20isolated%20on%20light%20background%2C%20clean%20minimalist%20ecommerce&width=200&height=200&seq=side-prod-4&orientation=squarish',
                ].map((src, i) => (
                  <div key={i} className="aspect-square rounded-lg overflow-hidden bg-white">
                    <img src={src} alt="product" className="w-full h-full object-cover hover:scale-110 transition-transform duration-300" />
                  </div>
                ))}
              </div>
            </div>

            {/* Related articles */}
            <div className="bg-stone-50 rounded-xl p-5">
              <h3 className="font-medium text-stone-900 mb-4">{t('article.relatedArticles')}</h3>
              <ul className="space-y-3">
                {relatedArticles.map((ra) => (
                  <li key={ra.slug}>
                    <a
                      href={`/article/${ra.slug}`}
                      className="block text-sm text-stone-600 hover:text-stone-900 transition-colors leading-snug"
                    >
                      {ra.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Table of contents */}
            <div className="bg-stone-50 rounded-xl p-5">
              <h3 className="font-medium text-stone-900 mb-4">{t('article.tableOfContents')}</h3>
              <ul className="space-y-2">
                {content.content
                  .filter((b) => b.type === 'h2')
                  .map((h2, i) => (
                    <li key={i}>
                      <span className="text-sm text-stone-500">{h2.text}</span>
                    </li>
                  ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* Related articles cards */}
      <section className="w-full px-6 lg:px-10 py-12 md:py-16 bg-stone-50">
        <h2 className="font-heading text-2xl md:text-3xl font-medium text-stone-900 mb-8">
          {t('article.relatedArticles')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {relatedArticles.map((ra) => (
            <a
              key={ra.slug}
              href={`/article/${ra.slug}`}
              className="group bg-white rounded-xl overflow-hidden border border-stone-100 hover:shadow-md transition-shadow"
            >
              <div className="aspect-[16/10] overflow-hidden relative">
                <img
                  src={ra.image}
                  alt={ra.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="p-5">
                <span className="inline-block px-3 py-1 bg-stone-100 text-stone-600 text-xs font-medium rounded-full mb-3">
                  {ra.category}
                </span>
                <h3 className="text-sm font-semibold text-stone-900 leading-snug group-hover:text-stone-700 transition-colors line-clamp-2">
                  {ra.title}
                </h3>
              </div>
            </a>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}